import {
  type AppUser,
  type CurrentUser,
  type Destination,
  api,
} from "@/lib/api"
import type { AdminTab, View } from "@/App"
import { AppSidebar } from "./AppSidebar"
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar"
import { DashboardPage } from "@/pages/DashboardPage"
import { VpnClientsPage } from "@/pages/VpnClientsPage"
import { VpnClientDetailPage } from "@/pages/VpnClientDetailPage"
import { ExportsPage } from "@/pages/ExportsPage"
import { AdminPage } from "@/pages/AdminPage"
import { AccountPage } from "@/pages/AccountPage"
import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { AlertTriangle } from "lucide-react"

type Props = {
  currentUser: CurrentUser
  machines: string[]
  sshCertificateReady: boolean
  selectedMachine: string
  activeView: View
  activeAdminTab: AdminTab
  detailClient: string | null
  onMachineChange: (m: string) => void
  onViewChange: (v: View, adminTab?: AdminTab) => void
  onAdminTabChange: (tab: AdminTab) => void
  onOpenClientDetail: (clientName: string) => void
  onLogout: () => void
  onUsersRefresh: (users: AppUser[]) => void
}

export function WorkspaceLayout({
  currentUser,
  machines,
  sshCertificateReady,
  selectedMachine,
  activeView,
  activeAdminTab,
  detailClient,
  onMachineChange,
  onViewChange,
  onAdminTabChange,
  onOpenClientDetail,
  onLogout,
  onUsersRefresh,
}: Props) {
  const [destinations, setDestinations] = useState<Destination[]>([])
  const [destinationsLoaded, setDestinationsLoaded] = useState(false)

  useEffect(() => {
    api
      .getDestinations()
      .then((r) => {
        setDestinations(r.destinations)
        setDestinationsLoaded(true)
      })
      .catch(() => {})
  }, [])

  const allMachines = destinationsLoaded
    ? destinations.filter((d) => d.is_active !== false).map((d) => d.machine_name)
    : machines

  useEffect(() => {
    if (!destinationsLoaded) return
    if (allMachines.length === 0) {
      if (selectedMachine) onMachineChange("")
      return
    }
    if (!allMachines.includes(selectedMachine)) onMachineChange(allMachines[0]!)
  }, [destinationsLoaded, allMachines.join("\u0000"), selectedMachine, onMachineChange])

  const handleDestinationsChange = (nextDestinations: Destination[]) => {
    setDestinations(nextDestinations)
    setDestinationsLoaded(true)
  }

  const contentKey =
    activeView === "dashboard"
      ? `${activeView}:${selectedMachine}`
      : activeView === "vpn-client-detail"
        ? `${activeView}:${selectedMachine}:${detailClient ?? ""}`
        : activeView

  return (
    <SidebarProvider className="flex min-h-svh w-full min-w-0">
      <AppSidebar
        currentUser={currentUser}
        machines={allMachines}
        selectedMachine={selectedMachine}
        activeView={activeView}
        onMachineChange={onMachineChange}
        onViewChange={onViewChange}
        onLogout={onLogout}
      />
      <SidebarInset className="overflow-hidden">
        <div className="flex h-svh flex-col overflow-hidden">
          <main
            key={contentKey}
            className="flex min-h-0 flex-1 flex-col overflow-y-auto animate-in fade-in-0 slide-in-from-bottom-2 duration-300 fill-mode-both motion-reduce:animate-none"
          >
            <div className="flex min-h-full w-full min-w-0 flex-1 flex-col">
              {!sshCertificateReady && (
                <div className="mx-4 mt-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm sm:mx-6 md:mx-8">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-500" />
                      <p className="text-foreground">
                        Nenhum certificado PEM foi configurado. As operacoes SSH vao falhar ate voce enviar o certificado.
                      </p>
                    </div>
                    {currentUser.permissions.includes("admin") && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onViewChange("admin", "certificates")}
                      >
                        Abrir certificados
                      </Button>
                    )}
                  </div>
                </div>
              )}
              {activeView === "dashboard" && (
                <DashboardPage
                  selectedMachine={selectedMachine}
                  currentUser={currentUser}
                />
              )}
              {activeView === "vpn-clients" && (
                <VpnClientsPage
                  selectedMachine={selectedMachine}
                  currentUser={currentUser}
                  onOpenClientDetail={onOpenClientDetail}
                />
              )}
              {activeView === "vpn-client-detail" && detailClient && (
                <VpnClientDetailPage
                  selectedMachine={selectedMachine}
                  currentUser={currentUser}
                  clientName={detailClient}
                  onBack={() => onViewChange("vpn-clients")}
                />
              )}
              {activeView === "exports" && (
                <ExportsPage machines={allMachines} currentUser={currentUser} />
              )}
              {activeView === "admin" && (
                <AdminPage
                  currentUser={currentUser}
                  destinations={destinations}
                  activeTab={activeAdminTab}
                  onTabChange={onAdminTabChange}
                  onDestinationsChange={handleDestinationsChange}
                  onMachineChange={onMachineChange}
                  onUsersRefresh={onUsersRefresh}
                />
              )}
              {activeView === "account" && (
                <AccountPage currentUser={currentUser} />
              )}
            </div>
          </main>
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}
